import { useDraggable } from "react-use-draggable-scroll";
import { FaStar } from "react-icons/fa";
import { useRef, MutableRefObject } from "react";
import { useGetHomeCustomersLayoutQuery } from "../../Services/CustomerPortalLayoutService";
import { useGetCategoryListQuery } from "../../Services/CategoryService";
import { ProductShowcase } from "../../Helpers/Interfaces/CustomerPortalModel";
import { useSelector } from "react-redux";
import { Category } from "../../Helpers/Interfaces/CategoryType";
import { Link, useNavigate } from "react-router-dom";
import Utils from "../../Utils/CommonProgram";
import Constant from "../../Config/Constant";

const Recommended = () => {
  const productShowcaseOne: ProductShowcase = useSelector(
    (state: any) => state.CustomerPortalLayoutSlice["productShowcase1"],
  );
  const categoryList: Category[] = useSelector(
    (state: any) => state.SettingSlice.categoryList,
  );
  /*
   * Phone Refill categories belong on the home strip too.
   *
   * They are kept in their own slice because `categoryList` is read by the
   * Gift Cards page, the sidebar, the banner carousel and product details --
   * putting refill categories in it would list them as gift cards in four
   * other places. So they are combined here, for this strip only.
   *
   * They were left out until now because the tile's link was hardcoded to
   * /gift-cards/, which would have sent a refill category to the wrong
   * listing. The link is now chosen from the parent.
   */
  const refillCategoryList: Category[] = useSelector(
    (state: any) => state.SettingSlice.refillCategoryList,
  );
  const homeCategories: Category[] = [
    ...(categoryList || []),
    ...(refillCategoryList || []),
  ];
  const countryDialingList: any[] = useSelector(
    (state: any) => state.SettingSlice.countryDialingList,
  );
  const navigate = useNavigate();

  useGetHomeCustomersLayoutQuery();
  useGetCategoryListQuery();

  // Reference for the categories container
  const categoriesRef: any =
    useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;

  // Use the draggable hook
  const { events } = useDraggable(categoriesRef);

  return (
    <div
      className={`w-full mx-auto md:py-10 py-6 md:px-16 px-0 transition-colors duration-300`}
      style={{ backgroundColor: productShowcaseOne?.bgColor || undefined }}
    >
      <div
        className={`md:mb-8 px-6 md:px-0 mb-4 ${productShowcaseOne?.isPublished === false || productShowcaseOne?.products.length === 0 ? "hidden" : "block"}`}
      >
        <h2
          className="text-xl font-bold tracking-tight"
          style={{ color: productShowcaseOne?.textColor || undefined }}
        >
          {productShowcaseOne?.title}
        </h2>
        <div className="h-1 w-12 bg-light-primary-500 rounded-full mt-2"></div>
      </div>

      {/*
        The products, gated on the products.

        This used to be hidden whenever `categoryList` was empty, so a home
        page section with a product in it rendered its heading and nothing
        else. The two are unrelated -- one is a product showcase, the other a
        category strip -- and `categoryList` holds only Gift Cards and Gaming
        categories (see CategoryService), so a site whose only category is a
        Phone Refill one showed no products at all.

        Invisible on the demo, which has 70 gift-card and gaming categories. A
        fresh environment shows it immediately.
      */}
      {productShowcaseOne?.isPublished === false ||
      (productShowcaseOne?.products?.length || 0) === 0 ? (
        <></>
      ) : (
        <>
          {/* Platforms */}
          <div
            className={`md:grid gap-6 mb-8 hidden p-1 ${
              (productShowcaseOne?.products?.length || 0) <= 3
                ? "md:grid-cols-3"
                : "md:grid-cols-4"
            }`}
          >
            {productShowcaseOne &&
              Array.isArray(productShowcaseOne?.products) &&
              productShowcaseOne?.products.map((item: any, index) => (
                <Link
                  key={index}
                  to={`/gift-cards/product-details/${item._id}/${item.productName}`}
                  className="group bg-white dark:bg-dark-background rounded-2xl overflow-hidden cursor-pointer border border-gray-100 dark:border-white/[0.06] hover:border-gray-200 dark:hover:border-white/10 shadow-sm hover:shadow-md dark:shadow-none transition-all duration-300 hover:-translate-y-0.5 block"
                >
                  <div className="p-3">
                    <div className="w-full mb-3 h-[143px] relative rounded-xl overflow-hidden flex items-center justify-center">
                      <div className="flex flex-row items-center gap-1.5 absolute top-2 left-2 z-10 flex-wrap">
                        {item.isFeatured && (
                          <span className="bg-light-primary-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                            Featured
                          </span>
                        )}
                        {item.isPaysafe && (
                          <span className="bg-orange-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                            Paysafe
                          </span>
                        )}
                        {item.cashBack ? (
                          <span className="bg-yellow-400 text-[10px] py-1 px-2.5 text-yellow-900 rounded-full font-bold flex flex-row items-center shadow-sm">
                            <img
                              src={`${process.env.PUBLIC_URL}/icons/cashback.svg`}
                              alt="Cashback"
                            />
                            &nbsp;{item.cashBack}%
                          </span>
                        ) : null}
                      </div>
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="w-full flex items-center justify-between text-sm">
                        <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white w-[80%] overflow-hidden text-ellipsis whitespace-nowrap">
                          {item.productName}
                        </h2>
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <span className="font-semibold text-sm">
                            {item.ratingValue
                              ? Number.isInteger(item.ratingValue)
                                ? item.ratingValue
                                : item.ratingValue.toFixed(1)
                              : "0"}
                          </span>
                          <FaStar className="w-3.5 h-3.5 text-yellow-500" />
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 font-medium dark:text-gray-400 block">
                        {Utils.getItemPriceRange(item)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>

          {/* Mobile Platforms (Scrollable) */}
          <div
            className="flex md:hidden overflow-x-auto gap-4 md:mb-8 mb-6 scrollbar-hide px-6"
            style={{
              scrollbarWidth: "none" /* Firefox */,
              msOverflowStyle: "none" /* IE and Edge */,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {productShowcaseOne &&
              productShowcaseOne?.products &&
              productShowcaseOne?.products.map((item: any, index) => (
                <Link
                  key={index}
                  to={`/gift-cards/product-details/${item._id}/${item.productName}`}
                  className="flex-shrink-0 w-[180px] group bg-white dark:bg-dark-background rounded-2xl overflow-hidden cursor-pointer border border-gray-100 dark:border-white/[0.06] hover:border-gray-200 dark:hover:border-white/10 shadow-sm hover:shadow-md dark:shadow-none transition-all duration-300 active:scale-[0.98] block"
                >
                  <div className="p-3">
                    <div className="w-full mb-3 h-[143px] relative rounded-xl overflow-hidden flex items-center justify-center">
                      <div className="flex flex-row items-center gap-1.5 absolute top-2 left-2 z-10 flex-wrap">
                        {item.isFeatured && (
                          <span className="bg-light-primary-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                            Featured
                          </span>
                        )}
                        {item.isPaysafe && (
                          <span className="bg-orange-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                            Paysafe
                          </span>
                        )}
                      </div>
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="w-full flex items-center justify-between text-sm">
                        <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white w-[80%] overflow-hidden text-ellipsis whitespace-nowrap">
                          {item.productName}
                        </h2>
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <span className="font-semibold text-sm">
                            {item.ratingValue
                              ? Number.isInteger(item.ratingValue)
                                ? item.ratingValue
                                : item.ratingValue.toFixed(1)
                              : "0"}
                          </span>
                          <FaStar className="w-3.5 h-3.5 text-yellow-500" />
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 font-medium dark:text-gray-400 block">
                        {Utils.getItemPriceRange(item)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </>
      )}

      {/*
        Categories, hidden when there are none.

        Hidden with CSS rather than unmounted, and that distinction matters:
        the strip below carries `categoriesRef`, which `useDraggable` reads on
        every render. The hook runs unconditionally at the top of this
        component, so unmounting the element leaves the ref undefined and the
        hook calls getComputedStyle(undefined) -- which throws and takes the
        whole home page down with it. Keeping the node mounted keeps the ref
        valid.

        Note this list is Gift Cards and Gaming only; Phone Refill categories
        belong to the refill page and are kept separately.
      */}
      <div className={homeCategories.length > 0 ? "" : "hidden"}>
      <div className="mb-4 px-6 md:px-0">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
          Categories
        </h3>
        <div className="h-1 w-10 bg-light-primary-500 rounded-full mt-2"></div>
      </div>
      <div className="relative md:mx-0">
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white dark:from-dark-background to-transparent z-10 pointer-events-none md:hidden"></div>
        <div
          ref={categoriesRef}
          {...events}
          className="flex gap-2.5 overflow-x-auto scrollbar-hide px-6 md:px-0"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div className="flex gap-2.5 pr-8 md:pr-0">
            {homeCategories &&
              homeCategories.map((category: any, index: number) => {
                const opacity =
                  1 - (index / Math.max(homeCategories.length - 1, 1)) * 0.35;
                return (
                  <Link
                    key={index}
                    to={
                      // A refill category has its own listing route; sending
                      // it to /gift-cards/ shows the wrong page.
                      category.parentCategoryName ===
                      Constant.PARENT_CATEGORY_NAME.PHONE_REFILL
                        ? `/refill/${category.categoryName}/${category.parentCategoryName}`
                        : `/gift-cards/${category.categoryName}/${category.parentCategoryName}`
                    }
                    className="flex flex-col items-center min-w-[80px] p-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0 active:scale-[0.97] hover:!opacity-100"
                    style={{
                      backgroundColor: `rgba(54, 96, 249, ${opacity})`,
                    }}
                  >
                    <img
                      src={category.icon}
                      alt={category.categoryName}
                      className="w-6 h-6 invert"
                    />
                    <span className="text-xs mt-1.5 text-center whitespace-nowrap text-white font-medium">
                      {category.categoryName}
                    </span>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Recommended;
